import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useAdminAuth } from "../../contexts/AdminAuthContext";
import api, { formatApiError } from "../../lib/apiClient";
import {
  LogOut,
  Search,
  Download,
  Users,
  CheckCircle2,
  Clock,
  Mail,
  Phone,
  Home,
  Calendar,
  Trash2,
  X,
  Save,
  RefreshCw,
  Loader2,
  BarChart3,
} from "lucide-react";

const STATUS_META = {
  nouveau: { label: "Nouveau", color: "bg-blue-100 text-blue-700 border-blue-200" },
  en_cours: { label: "En cours", color: "bg-amber-100 text-amber-700 border-amber-200" },
  traite: { label: "Traite", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
};

const APT_TYPES = ["", "F2", "F3", "F4", "Penthouse"];

function formatDate(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

const StatCard = ({ icon: Icon, label, value, accent = "amber", testId }) => (
  <div
    data-testid={testId}
    className="bg-white rounded-2xl border border-stone-200 p-5 shadow-sm hover:shadow-md transition"
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">{label}</p>
        <p className="text-3xl font-bold text-stone-900 mt-2">{value}</p>
      </div>
      <div
        className={`w-12 h-12 rounded-xl flex items-center justify-center ${
          accent === "amber"
            ? "bg-amber-100 text-amber-600"
            : accent === "blue"
            ? "bg-blue-100 text-blue-600"
            : accent === "green"
            ? "bg-emerald-100 text-emerald-600"
            : "bg-stone-100 text-stone-600"
        }`}
      >
        <Icon className="w-6 h-6" />
      </div>
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.nouveau;
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${meta.color}`}>
      {meta.label}
    </span>
  );
};

const ContactDetailModal = ({ contact, onClose, onUpdate, onDelete }) => {
  const [status, setStatus] = useState(contact.status || "nouveau");
  const [notes, setNotes] = useState(contact.notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    await onUpdate(contact.id, { status, notes });
    setSaving(false);
    onClose();
  };

  return (
    <div
      data-testid="contact-detail-modal"
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-stone-200">
          <div>
            <h2 className="text-xl font-bold text-stone-900">{contact.name}</h2>
            <p className="text-sm text-stone-500">{formatDate(contact.timestamp)}</p>
          </div>
          <button
            data-testid="close-modal-btn"
            onClick={onClose}
            className="w-9 h-9 rounded-lg hover:bg-stone-100 flex items-center justify-center transition"
          >
            <X className="w-5 h-5 text-stone-600" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="text-xs text-stone-500">Email</p>
                <a href={`mailto:${contact.email}`} className="text-stone-900 font-medium hover:text-amber-600 break-all">
                  {contact.email}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="text-xs text-stone-500">Telephone</p>
                <a href={`tel:${contact.phone}`} className="text-stone-900 font-medium hover:text-amber-600">
                  {contact.phone}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Home className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="text-xs text-stone-500">Type recherche</p>
                <p className="text-stone-900 font-medium">{contact.apartment_type}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="text-xs text-stone-500">Date</p>
                <p className="text-stone-900 font-medium">{formatDate(contact.timestamp)}</p>
              </div>
            </div>
          </div>

          {contact.message && (
            <div>
              <label className="block text-xs font-medium text-stone-500 uppercase tracking-wider mb-2">
                Message du client
              </label>
              <p className="text-stone-800 bg-stone-50 rounded-lg p-3 border border-stone-200 whitespace-pre-wrap">
                {contact.message}
              </p>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-stone-500 uppercase tracking-wider mb-2">
              Statut
            </label>
            <div className="flex gap-2 flex-wrap">
              {Object.entries(STATUS_META).map(([key, meta]) => (
                <button
                  key={key}
                  data-testid={`status-option-${key}`}
                  onClick={() => setStatus(key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${
                    status === key
                      ? meta.color + " ring-2 ring-offset-1 ring-stone-400"
                      : "bg-white text-stone-600 border-stone-200 hover:border-stone-400"
                  }`}
                >
                  {meta.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-500 uppercase tracking-wider mb-2">
              Notes internes
            </label>
            <textarea
              data-testid="contact-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="Ajoutez vos notes, prochaine action, suivi commercial..."
              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none resize-none"
            />
          </div>
        </div>

        <div className="flex items-center justify-between p-6 border-t border-stone-200 bg-stone-50 rounded-b-2xl">
          <button
            data-testid="delete-contact-btn"
            onClick={() => {
              if (window.confirm("Supprimer definitivement ce contact ?")) {
                onDelete(contact.id);
                onClose();
              }
            }}
            className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition font-medium"
          >
            <Trash2 className="w-4 h-4" />
            Supprimer
          </button>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 text-stone-600 hover:bg-stone-100 rounded-lg transition font-medium">
              Annuler
            </button>
            <button
              data-testid="save-contact-btn"
              onClick={save}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white rounded-lg font-semibold shadow-md disabled:opacity-60"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Enregistrer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { user, logout } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  // Filters
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [aptType, setAptType] = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (search) params.q = search;
      if (status) params.status = status;
      if (aptType) params.apartment_type = aptType;
      params.limit = 200;
      const [contactsRes, statsRes] = await Promise.all([
        api.get("/admin/contacts", { params }),
        api.get("/admin/stats"),
      ]);
      setContacts(contactsRes.data.items);
      setTotal(contactsRes.data.total);
      setStats(statsRes.data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [search, status, aptType]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const updateContact = async (id, payload) => {
    try {
      const { data } = await api.patch(`/admin/contacts/${id}`, payload);
      setContacts((prev) => prev.map((c) => (c.id === id ? data : c)));
      // refresh stats
      try {
        const { data: s } = await api.get("/admin/stats");
        setStats(s);
      } catch {}
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  const deleteContact = async (id) => {
    try {
      await api.delete(`/admin/contacts/${id}`);
      setContacts((prev) => prev.filter((c) => c.id !== id));
      setTotal((t) => Math.max(0, t - 1));
      try {
        const { data: s } = await api.get("/admin/stats");
        setStats(s);
      } catch {}
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await api.get("/admin/contacts/export", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contacts_ryad_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  const maxDaily = useMemo(() => {
    if (!stats?.daily_last30?.length) return 1;
    return Math.max(...stats.daily_last30.map((d) => d.count), 1);
  }, [stats]);

  return (
    <div data-testid="admin-dashboard-page" className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center shadow">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-stone-900">Dashboard Admin</h1>
              <p className="text-xs text-stone-500">Residence RYAD - {user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="refresh-btn"
              onClick={fetchAll}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-2 text-stone-600 hover:bg-stone-100 rounded-lg transition text-sm font-medium disabled:opacity-60"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              <span className="hidden md:inline">Actualiser</span>
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="flex items-center gap-2 px-3 py-2 text-stone-600 hover:bg-red-50 hover:text-red-600 rounded-lg transition text-sm font-medium"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden md:inline">Deconnexion</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Stats */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard testId="stat-total" icon={Users} label="Total contacts" value={stats?.total ?? "-"} accent="amber" />
          <StatCard testId="stat-week" icon={Calendar} label="7 derniers jours" value={stats?.last7_days ?? "-"} accent="blue" />
          <StatCard testId="stat-new" icon={Clock} label="Nouveaux" value={stats?.by_status?.nouveau ?? 0} accent="blue" />
          <StatCard testId="stat-done" icon={CheckCircle2} label="Traites" value={stats?.by_status?.traite ?? 0} accent="green" />
        </section>

        {/* Charts row */}
        {stats && (
          <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* By type */}
            <div className="lg:col-span-1 bg-white rounded-2xl border border-stone-200 p-5">
              <h3 className="text-sm font-semibold text-stone-700 mb-4 uppercase tracking-wider">
                Par type d'appart
              </h3>
              <div className="space-y-3">
                {(stats.by_apartment_type || []).map((item) => {
                  const pct = stats.total ? (item.count / stats.total) * 100 : 0;
                  return (
                    <div key={item.type}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-medium text-stone-700">{item.type}</span>
                        <span className="text-stone-500">
                          {item.count} <span className="text-xs">({pct.toFixed(0)}%)</span>
                        </span>
                      </div>
                      <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
                {stats.by_apartment_type?.length === 0 && (
                  <p className="text-sm text-stone-400">Aucune donnee</p>
                )}
              </div>
            </div>

            {/* Daily 30 days */}
            <div className="lg:col-span-2 bg-white rounded-2xl border border-stone-200 p-5">
              <h3 className="text-sm font-semibold text-stone-700 mb-4 uppercase tracking-wider">
                Demandes (30 derniers jours)
              </h3>
              {stats.daily_last30?.length ? (
                <div className="flex items-end gap-1 h-32">
                  {stats.daily_last30.map((d) => (
                    <div
                      key={d.date}
                      className="flex-1 flex flex-col items-center gap-1 group"
                      title={`${d.date}: ${d.count}`}
                    >
                      <div
                        className="w-full bg-gradient-to-t from-amber-500 to-amber-300 rounded-sm hover:from-amber-600 hover:to-amber-400 transition"
                        style={{ height: `${(d.count / maxDaily) * 100}%`, minHeight: "2px" }}
                      />
                      <span className="text-[9px] text-stone-400 rotate-45 origin-top-left translate-y-2 hidden group-hover:inline-block">
                        {d.date.slice(5)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-stone-400">Aucune demande dans les 30 derniers jours.</p>
              )}
            </div>
          </section>
        )}

        {/* Filters */}
        <section className="bg-white rounded-2xl border border-stone-200 p-5">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher par nom, email, telephone, message..."
                className="w-full pl-10 pr-3 py-2.5 border border-stone-300 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none"
              />
            </div>
            <select
              data-testid="filter-status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="px-3 py-2.5 border border-stone-300 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none bg-white"
            >
              <option value="">Tous statuts</option>
              <option value="nouveau">Nouveau</option>
              <option value="en_cours">En cours</option>
              <option value="traite">Traite</option>
            </select>
            <select
              data-testid="filter-type"
              value={aptType}
              onChange={(e) => setAptType(e.target.value)}
              className="px-3 py-2.5 border border-stone-300 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none bg-white"
            >
              {APT_TYPES.map((t) => (
                <option key={t} value={t}>{t || "Tous types"}</option>
              ))}
            </select>
            <button
              data-testid="export-csv-btn"
              onClick={downloadCsv}
              className="flex items-center gap-2 px-4 py-2.5 bg-stone-900 hover:bg-stone-800 text-white rounded-lg font-medium transition"
            >
              <Download className="w-4 h-4" />
              <span className="hidden md:inline">Export CSV</span>
            </button>
          </div>
        </section>

        {/* Table */}
        <section className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-stone-700">
              {total} contact{total > 1 ? "s" : ""}{" "}
              {(search || status || aptType) && <span className="text-stone-400 font-normal">(filtres appliques)</span>}
            </h3>
          </div>

          {error && (
            <div className="p-5 text-sm text-red-600 bg-red-50 border-b border-red-200">{error}</div>
          )}

          <div className="overflow-x-auto">
            <table data-testid="contacts-table" className="w-full text-sm">
              <thead className="bg-stone-50 text-xs uppercase tracking-wider text-stone-500">
                <tr>
                  <th className="text-left px-5 py-3 font-medium">Date</th>
                  <th className="text-left px-5 py-3 font-medium">Nom</th>
                  <th className="text-left px-5 py-3 font-medium">Contact</th>
                  <th className="text-left px-5 py-3 font-medium">Type</th>
                  <th className="text-left px-5 py-3 font-medium">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-16 text-center">
                      <Loader2 className="w-6 h-6 animate-spin text-amber-500 mx-auto" />
                    </td>
                  </tr>
                ) : contacts.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-16 text-center text-stone-400">
                      Aucun contact trouve
                    </td>
                  </tr>
                ) : (
                  contacts.map((c) => (
                    <tr
                      key={c.id}
                      data-testid={`contact-row-${c.id}`}
                      onClick={() => setSelected(c)}
                      className="hover:bg-amber-50/50 cursor-pointer transition"
                    >
                      <td className="px-5 py-3 text-stone-600 whitespace-nowrap">{formatDate(c.timestamp)}</td>
                      <td className="px-5 py-3">
                        <p className="font-medium text-stone-900">{c.name}</p>
                        {c.notes && (
                          <p className="text-xs text-stone-400 truncate max-w-[240px]">note: {c.notes}</p>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <p className="text-stone-700">{c.email}</p>
                        <p className="text-xs text-stone-500">{c.phone}</p>
                      </td>
                      <td className="px-5 py-3">
                        <span className="px-2 py-1 bg-amber-50 text-amber-700 rounded-md text-xs font-medium border border-amber-100">
                          {c.apartment_type}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <StatusBadge status={c.status} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      {selected && (
        <ContactDetailModal
          contact={selected}
          onClose={() => setSelected(null)}
          onUpdate={updateContact}
          onDelete={deleteContact}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
